import React, { Context, createContext, useState, useContext } from 'react';
import {
  INotificationContext,
  INotification,
  ENotificationTypes,
} from '../Types';
import { v4 as uuidv4 } from 'uuid';
import { useHistory } from 'react-router';
import { pathname } from '../utils';
import { useEventTrackerContext } from './EventTracker';
import Notification from '../components/BuildingBlocks/Notifications/Notification';

export const NotificationsContext: Context<INotificationContext> =
  createContext({
    notifications: null,
    addNotification: () => {},
    removeNotification: () => {},
  });

export default function NotificationsContextProvider(props) {
  const history = useHistory();
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [canShowNotification, setCanShowNotification] = useState(true);
  const { trackEvent } = useEventTrackerContext();

  function handleDismiss(id: string) {
    setOpen(false);
    setTimeout(() => {
      const filteredNotifications = notifications.filter(
        (notification) => notification.id !== id,
      );
      setNotifications(filteredNotifications);
      if (filteredNotifications.length > 0) {
        setOpen(true);
      }
    }, 450);
  }

  function addNotification(notification: INotification) {
    notification['id'] = uuidv4();

    setNotifications((currentNotifications) => {
      // REMOVE DUPLICATES
      currentNotifications = currentNotifications.filter(
        (currentNotification) =>
          currentNotification.message !== notification.message ||
          currentNotification.lineId !== notification.lineId,
      );
      // SET IN STATE
      return [notification, ...currentNotifications];
    });
    if (!open) {
      setOpen(true);
    }
  }

  function removeNotification(type: ENotificationTypes, lineId: string) {
    setNotifications((currentNotifications) => {
      if (ENotificationTypes.application === type) {
        return currentNotifications.filter(
          (notification) =>
            notification.type !== ENotificationTypes.application,
        );
      }
      if (ENotificationTypes.line === type) {
        return currentNotifications.filter(
          (notification) => notification.lineId !== lineId,
        );
      }
      return currentNotifications;
    });
  }

  function canNotificationShowOnPage(currentPage: string) {
    if (
      currentPage === pathname.lines.url ||
      currentPage === pathname.submitApplication.url
    ) {
      setCanShowNotification(true);
    } else {
      setCanShowNotification(false);
    }
  }

  history.listen((location) => {
    canNotificationShowOnPage(location.pathname);
  });

  return (
    <NotificationsContext.Provider
      value={{ notifications, addNotification, removeNotification }}
    >
      <>
        {props.children}
        {notifications.length > 0 && canShowNotification && (
          <Notification
            handleClose={handleDismiss}
            open={open}
            notification={notifications[0]}
            trackEvent={trackEvent}
          />
        )}
      </>
    </NotificationsContext.Provider>
  );
}

export const useNotificationsContext = () => useContext(NotificationsContext);
